export interface PdfScrollKey {
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
}

export type PdfScrollCommand =
  | "up"
  | "down"
  | "left"
  | "right"
  | "page-up"
  | "page-down"
  | "start"
  | "end";

const plainCommands: Readonly<Record<string, PdfScrollCommand>> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  PageUp: "page-up",
  PageDown: "page-down",
  Home: "start",
  End: "end",
};
const controlCommands: Readonly<Record<string, PdfScrollCommand>> = { Home: "start", End: "end" };
const metaCommands: Readonly<Record<string, PdfScrollCommand>> = {
  ArrowUp: "start",
  ArrowDown: "end",
};

function modifiedCommand(event: PdfScrollKey): PdfScrollCommand | null {
  if (event.shiftKey) return null;
  const commands = event.ctrlKey ? controlCommands : metaCommands;
  return commands[event.key] ?? null;
}

function unmodifiedCommand(event: PdfScrollKey): PdfScrollCommand | null {
  if (event.key === " ") return event.shiftKey ? "page-up" : "page-down";
  if (event.shiftKey) return null;
  return plainCommands[event.key] ?? null;
}

/** Only scrolling shortcuts; editing, selection and browser shortcuts stay native. */
export function pdfScrollCommand(event: PdfScrollKey): PdfScrollCommand | null {
  if (event.altKey || (event.ctrlKey && event.metaKey)) return null;
  return event.ctrlKey || event.metaKey ? modifiedCommand(event) : unmodifiedCommand(event);
}

export function preservesPdfKeyTarget(target: Element, key: string): boolean {
  if (
    target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"], [role="combobox"], [role="slider"], [role="spinbutton"], [role="listbox"], [role="menu"]'
    )
  )
    return true;
  return (
    key === " " &&
    Boolean(
      target.closest(
        'button, [role="button"], [role="checkbox"], [role="switch"], [role="radio"], [role="tab"]'
      )
    )
  );
}

export function scrollPdfViewport(scroller: HTMLElement, command: PdfScrollCommand): void {
  if (command === "start" || command === "end") {
    scroller.scrollTo({ top: command === "start" ? 0 : scroller.scrollHeight });
    return;
  }
  const page = scroller.clientHeight * 0.875;
  const offsets: Record<
    Exclude<PdfScrollCommand, "start" | "end">,
    { top?: number; left?: number }
  > = {
    up: { top: -40 },
    down: { top: 40 },
    left: { left: -40 },
    right: { left: 40 },
    "page-up": { top: -page },
    "page-down": { top: page },
  };
  scroller.scrollBy(offsets[command]);
}
